// Constellation
import { Box, Button, Collapsible, Dropdown, Heading, IconButton } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';

// Flux
import { useAppSelector } from '../../flux/configureStore';

// Styles
import { chevronContainerStyles, containerStyles } from './styles';

import type React from 'react';

/**
 * FilterWrapper wraps each filter popover with Popper logic (desktop), header styling,
 * done button (desktop), possible Collapsible header (mWeb), and other styles.
 *
 * Note: in order for the Dropdown to function properly, filter components must return
 * FilterWrapper called functionally - ex: FilterWrapper({ heading, children })
 */
const FilterWrapper = ({
  children,
  heading,
  useCollapse,
}: {
  children: React.ReactNode;
  heading: string | React.ReactNode;
  useCollapse?: boolean;
}) => {
  const isMobile: boolean = useAppSelector((state) => state.app.device.isMobile);
  const isMdScreen = useAppSelector((state) => state.app.device.screenWidth) === 'md';
  const isMobileOrTablet = isMobile || isMdScreen;

  const renderDesktopWrapper = (
    <Box css={containerStyles}>
      <Dropdown.Body>
        <Heading level={4} css={{ textStyle: 'body-bold' }}>
          {heading}
        </Heading>
        {children}
      </Dropdown.Body>
      <Dropdown.Footer css={{ background: 'bg.accent.brand.hero' }}>
        <Dropdown.Close>
          <Button size="sm">Done</Button>
        </Dropdown.Close>
      </Dropdown.Footer>
    </Box>
  );

  const renderMobileWrapper = (
    <Box css={containerStyles}>
      <Heading level={4} css={{ textStyle: 'body-bold', marginLeft: '600', marginTop: '600' }}>
        {heading}
      </Heading>
      {children}
    </Box>
  );

  const renderMobileWrapperWithCollapse = (
    <Box css={containerStyles}>
      <Collapsible>
        {(collapsed: boolean, toggle: () => void) => (
          <>
            <Box css={{ margin: '{spacing.400} 0 {spacing.200} {spacing.600}' }}>
              {heading}
              <IconButton
                onClick={toggle}
                title={collapsed ? 'Expand' : 'Collapse'}
                icon={collapsed ? <IconChevronDownFilled /> : <IconChevronUpFilled />}
                tone="brand"
                emphasis="bare"
                size="md"
                css={chevronContainerStyles}
              />
            </Box>
            {!collapsed && children}
          </>
        )}
      </Collapsible>
    </Box>
  );

  return isMobileOrTablet
    ? useCollapse
      ? renderMobileWrapperWithCollapse
      : renderMobileWrapper
    : renderDesktopWrapper;
};

export default FilterWrapper;
